import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { acceptInvitation, getInvitation } from '../api/authApi';
import { PasswordPolicyHints } from '../components/PasswordPolicyHints';
import {
  canonicalPasswordFailureI18nKeys,
  canonicalPasswordFailures,
} from '../utils/canonicalPassword';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';

function getActivationErrorMessage(err, t) {
  return safeApiErrorMessage(err, t('auth:activation_failed'));
}

/**
 * Localized accept-page status — never interpolates raw EN enums into UI copy.
 */
function invitationAcceptStatusMessage(t, preview) {
  const messageKey = preview?.message_key;
  if (typeof messageKey === 'string' && messageKey.startsWith('customerInvitations.')) {
    return t(messageKey.replace(/^customerInvitations\./, 'customerInvitations:'), {
      defaultValue: '',
    }) || null;
  }

  const status = preview?.invitation_status || 'invalid';
  const keyByStatus = {
    accepted: 'customerInvitations:accept.alreadyAccepted',
    expired: 'customerInvitations:accept.expired',
    cancelled: 'customerInvitations:accept.cancelled',
    revoked: 'customerInvitations:accept.cancelled',
    pending: 'customerInvitations:accept.pending',
    invalid: 'customerInvitations:accept.invalid',
  };

  return t(keyByStatus[status] || 'customerInvitations:accept.invalid');
}

function invitationAcceptAlertSeverity(status) {
  if (status === 'accepted') return 'info';
  if (status === 'pending') return 'info';
  return 'error';
}

export function AcceptInvitationPage() {
  const { t } = useTranslation(['auth', 'common', 'customerInvitations', 'validation']);
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', password: '', password_confirmation: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getInvitation(token)
      .then((data) => {
        setPreview(data);
        setForm((prev) => ({ ...prev, name: data.name || '' }));
      })
      .catch(() => setError(t('auth:invitation_not_found')))
      .finally(() => setLoading(false));
  }, [token, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const passwordFailures = canonicalPasswordFailures(form.password, form.password_confirmation);
    if (passwordFailures.length > 0) {
      setError(canonicalPasswordFailureI18nKeys(passwordFailures).map((key) => t(key)).join(', '));
      return;
    }

    setSubmitting(true);

    try {
      await acceptInvitation(token, form);
      navigate('/login', {
        replace: true,
        state: { message: t('auth:account_activated_sign_in') },
      });
    } catch (err) {
      setError(getActivationErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!preview || preview.invitation_status !== 'pending') {
    const status = preview?.invitation_status || 'invalid';
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ maxWidth: 480, width: '100%' }}>
          <CardContent>
            <Alert severity={invitationAcceptAlertSeverity(status)}>
              {error || invitationAcceptStatusMessage(t, preview) || t('customerInvitations:accept.invalid')}
            </Alert>
            <Button component={RouterLink} to="/login" sx={{ mt: 2 }}>
              {t('auth:go_to_login')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>{t('auth:accept_invitation')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth:join_as', { customer: preview.customer_name, role: preview.role })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('auth:email')}: {preview.email}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            {t('auth:expires')}: {new Date(preview.expires_at).toLocaleString()}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label={t('auth:your_name')}
              fullWidth
              margin="normal"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label={t('auth:password')}
              type="password"
              fullWidth
              required
              margin="normal"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <TextField
              label={t('auth:confirm_password')}
              type="password"
              fullWidth
              required
              margin="normal"
              autoComplete="new-password"
              value={form.password_confirmation}
              onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
            />
            <PasswordPolicyHints
              password={form.password}
              confirmation={form.password_confirmation}
              t={t}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              disabled={submitting || canonicalPasswordFailures(form.password, form.password_confirmation).length > 0}
            >
              {submitting ? t('auth:activating') : t('auth:activate_account')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
